import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { FirebaseError } from 'firebase/app'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import { useQueryClient } from '@tanstack/react-query'

import { authErrorMessages } from '../constants/errorStrings'
import { authStrings } from '../constants/strings'
import { auth } from '../lib/firebase'
import { signOutCompletely } from '../lib/signOut'
import {
  clearTermsAcceptanceUnconfirmed,
  clearTermsConsentSignOutPending,
} from '../lib/termsGate'
import { getTermsErrorCode, getTermsStatus, TERMS_ERROR_CODES } from '../api/terms'
import { getApiErrorCode } from '../utils/apiErrorCode'
import { termsStatusQueryKey } from 'hooks/useTermsStatus'
import useMessageStore from 'store/useMessageStore'

// Convert error message to user-friendly message
const getAuthErrorMessage = (error: unknown) => {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-email':
        return authErrorMessages.invalidEmail
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return authErrorMessages.invalidCredentials
      case 'auth/too-many-requests':
        return authErrorMessages.tooManyRequests
      default:
        return authErrorMessages.loginFailedCheckInput
    }
  }

  return authErrorMessages.loginFailed
}

/**
 * 非同期処理の開始時の利用者。await の後に `isCurrent()` が false なら、その間にログイン・ログアウト・
 * 利用者の切り替えが起きているので、今の利用者のキャッシュ・ログイン状態・画面遷移を変えずに中止する。
 */
export type AuthSession = {
  uid: string | undefined
  isCurrent: () => boolean
}

type AuthContextValue = {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<string | undefined>
  signOut: () => Promise<string | undefined>
  /** 今の Firebase の利用者でセッションを捕まえる（利用者が居なければ uid は undefined） */
  captureSession: () => AuthSession
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Keep authentication status and initial loading state
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const queryClient = useQueryClient()
  // ログイン試行・ログアウトのたびに進める。古い試行の遅い応答で、今の利用者の状態を変えない
  const sessionEpochRef = useRef(0)
  // 直前に観測した利用者（undefined は初回の状態確認前）
  const observedUidRef = useRef<string | null | undefined>(undefined)

  /** 利用者が変わった。前の利用者の業務・規約の状態（取得中を含む）を使わない */
  const discardPreviousUserState = useCallback(() => {
    void queryClient.cancelQueries()
    queryClient.clear()
    useMessageStore.getState().resetStore()
  }, [queryClient])

  /** epoch（ログイン試行・ログアウトの世代）と UID が両方とも変わっていない間だけ current */
  const sessionGuard = useCallback(
    (epoch: number, uid: string | undefined): AuthSession => ({
      uid,
      isCurrent: () =>
        epoch === sessionEpochRef.current &&
        uid !== undefined &&
        auth.currentUser?.uid === uid,
    }),
    [],
  )

  const captureSession = useCallback(
    () => sessionGuard(sessionEpochRef.current, auth.currentUser?.uid),
    [sessionGuard],
  )

  // Sign in
  const signIn = useCallback(async (email: string, password: string) => {
    const epoch = ++sessionEpochRef.current
    let attemptUid: string | undefined
    // この試行の後に別のログイン・ログアウト・利用者の切り替えが起きていないか
    const isCurrentAttempt = () => sessionGuard(epoch, attemptUid).isCurrent()
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password)
      attemptUid = credential.user.uid
      if (!isCurrentAttempt()) return undefined
      // 新しいログインなので、前回の同意保存後のログアウト待ちは完了扱いにする
      clearTermsConsentSignOutPending()
      // 本人の同意の送信結果が未確認でも、ログインし直したので完了扱いにする（別の利用者の印には触れない）
      clearTermsAcceptanceUnconfirmed(attemptUid)
      // 組織を選ぶ前に本人の同意状態を確認する。同意が必要なら RequireAuth が同意画面へ案内する
      const termsStatus = await getTermsStatus(attemptUid)
      if (!isCurrentAttempt()) return undefined
      queryClient.setQueryData(termsStatusQueryKey(attemptUid), termsStatus)
      return undefined
    } catch (caught) {
      // 古い試行の失敗で、後からログインした利用者をログアウトさせない
      if (attemptUid !== undefined && !isCurrentAttempt()) return undefined
      // 同意前の 403/503 はパスワード不正ではない。ログアウトせず、同意状態を取り直して同意画面へ案内する
      const termsCode = getTermsErrorCode(caught)
      if (
        attemptUid !== undefined &&
        (termsCode === TERMS_ERROR_CODES.acceptanceRequired ||
          termsCode === TERMS_ERROR_CODES.unavailable)
      ) {
        await queryClient.invalidateQueries({
          queryKey: termsStatusQueryKey(attemptUid),
        })
        return undefined
      }
      if (attemptUid !== undefined && auth.currentUser?.uid === attemptUid) {
        await firebaseSignOut(auth)
      }
      // 停止中は ApiErrorWatcher がログイン画面に停止の案内を出すので、エラー文言を重ねない
      if (getApiErrorCode(caught) === 'ACCOUNT_PAUSED') return undefined
      if (caught instanceof FirebaseError) {
        return getAuthErrorMessage(caught)
      }
      if (caught instanceof Error && caught.message) {
        return caught.message
      }
      return authErrorMessages.loginFailed
    }
  }, [queryClient, sessionGuard])

  // Sign out
  const signOut = useCallback(async () => {
    const epoch = ++sessionEpochRef.current
    const result = await signOutCompletely(auth.currentUser?.uid)
    if (!result.ok) return authStrings.signOutFailed
    // ログアウトの完了を待つ間に別のログインが始まっていたら、その利用者の状態は消さない
    if (epoch === sessionEpochRef.current && auth.currentUser === null) {
      discardPreviousUserState()
    }
    return undefined
  }, [discardPreviousUserState])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (nextUser) => {
        const nextUid = nextUser?.uid ?? null
        // 他のタブでのログアウト・別アカウントへの切り替えを含め、利用者が変わったら前の状態を捨てる
        if (
          observedUidRef.current !== undefined &&
          observedUidRef.current !== nextUid
        ) {
          discardPreviousUserState()
        }
        observedUidRef.current = nextUid
        setUser(nextUser)
        setLoading(false)
      },
      () => {
        setLoading(false)
      }
    )

    return unsubscribe
  }, [discardPreviousUserState])

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, captureSession }),
    [user, loading, signIn, signOut, captureSession]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    // Provider配下でのみ利用できるようにする
    throw new Error(authStrings.useAuthProviderError)
  }
  return context
}
